import { getConnectorWireCompatibility } from './compatibilityMatrix';
import { compatibilityMatrix } from './compatibilityMatrix';

// Wire type definitions with descriptions
export const wireTypes = {
  'PSM': {
    id: 'PSM',
    label: 'PSM',
    description: 'Professional Shielded Microphone cable',
    category: 'audio'
  },
  'PSI': {
    id: 'PSI',
    label: 'PSI',
    description: 'Professional Shielded Instrument cable',
    category: 'audio'
  },
  'PSZ': {
    id: 'PSZ',
    label: 'PSZ',
    description: 'Professional Shielded Zip Cable',
    category: 'zip'
  },
  'PSH': {
    id: 'PSH',
    label: 'PSH',
    description: 'Professional Shielded Hybrid Cable',
    category: 'hybrid'
  },
  'PSS': {
    id: 'PSS',
    label: 'PSS',
    description: 'Professional Shielded Speaker Cable',
    category: 'speaker'
  },
  'PSS12': {
    id: 'PSS12',
    label: 'PSS12',
    description: 'Professional Shielded Speaker Cable 12 AWG',
    category: 'speaker'
  },
  'PSS14': {
    id: 'PSS14',
    label: 'PSS14',
    description: 'Professional Shielded Speaker Cable 14 AWG',
    category: 'speaker'
  },
  'PSS16': {
    id: 'PSS16',
    label: 'PSS16',
    description: 'Professional Shielded Speaker Cable 16 AWG',
    category: 'speaker'
  },
  'PSP': {
    id: 'PSP',
    label: 'PSP',
    description: 'Professional Shielded Power Cable',
    category: 'power'
  },
  'PSP12': {
    id: 'PSP12',
    label: 'PSP12',
    description: 'Professional Shielded Power Cable 12 AWG',
    category: 'power'
  },
  'PSP14': {
    id: 'PSP14',
    label: 'PSP14',
    description: 'Professional Shielded Power Cable 14 AWG',
    category: 'power'
  },
  'PSP16': {
    id: 'PSP16',
    label: 'PSP16',
    description: 'Professional Shielded Power Cable 16 AWG',
    category: 'power'
  },
  'Zip': {
    id: 'Zip',
    label: 'Zip',
    description: 'Zip cable (bundled cables)',
    category: 'zip'
  },
  'PSDMX': {
    id: 'PSDMX',
    label: 'PSDMX',
    description: 'Professional Shielded DMX cable',
    category: 'dmx'
  }
};

// Get available wire types for a connector
export function getAvailableWireTypes(connectorId) {
  if (!connectorId) return [];
  
  const compatibleWires = getConnectorWireCompatibility(connectorId);
  return compatibleWires.map(wireId => wireTypes[wireId]).filter(Boolean);
}

// Get connector pole count from compatibility matrix
function getConnectorPoleCount(connectorId) {
  if (!connectorId) return null;
  
  const connectorData = compatibilityMatrix[connectorId];
  if (!connectorData) return null;
  
  // Check for both 'poles' and 'polesUsed' properties
  return connectorData.poles || connectorData.polesUsed || null;
}

// Determine optimal wire type based on connector pole counts
export function getOptimalWireTypeForConnectors(connectorIds) {
  if (!connectorIds || connectorIds.length === 0) return null;
  
  // Get pole counts for all connectors
  const poleCounts = connectorIds.map(id => getConnectorPoleCount(id)).filter(count => count !== null);
  
  if (poleCounts.length === 0) return null;
  
  // Check if any connector is a DMX-only connector (e.g. 5-pin DMX).
  // Do NOT treat multi-purpose XLR (e.g. purpose 'audio, hybrid, dmx') as DMX — user chose Audio cable.
  const hasDMXConnector = connectorIds.some(id => {
    const connectorData = compatibilityMatrix[id];
    if (!connectorData || !connectorData.purpose) return false;
    const purpose = connectorData.purpose.toLowerCase().trim();
    // Only force PSDMX when purpose is exclusively 'dmx' (dedicated DMX connectors).
    return purpose === 'dmx';
  });
  
  // If any connector is a DMX connector, use PSDMX
  if (hasDMXConnector) {
    return 'PSDMX';
  }
  
  // Check if any connector is a speaker connector
  const hasSpeakerConnector = connectorIds.some(id => {
    const connectorData = compatibilityMatrix[id];
    if (!connectorData) return false;
    
    // Check if connector is speaker-related
    const isSpeakerType = connectorData.type === 'speaker' || 
                         connectorData.type === 'Speakon' ||
                         connectorData.family === 'Speakon' ||
                         (connectorData.purpose && connectorData.purpose.toLowerCase().includes('speaker'));
    
    return isSpeakerType;
  });
  
  // If any connector is a speaker connector, use speaker wire type
  if (hasSpeakerConnector) {
    // For speaker connectors, determine the appropriate wire type based on pole count
    // Get the pole count of the speaker connector(s)
    const speakerPoleCounts = connectorIds
      .map(id => {
        const connectorData = compatibilityMatrix[id];
        if (!connectorData) return null;
        
        const isSpeakerType = connectorData.type === 'speaker' || 
                             connectorData.type === 'Speakon' ||
                             connectorData.family === 'Speakon' ||
                             (connectorData.purpose && connectorData.purpose.toLowerCase().includes('speaker'));
        
        return isSpeakerType ? (connectorData.poles || connectorData.polesUsed) : null;
      })
      .filter(count => count !== null);
    
    // Choose wire type based on pole count
    if (speakerPoleCounts.some(count => count >= 8)) {
      return 'PSS12'; // 8-pole speaker connectors get 12 AWG
    } else if (speakerPoleCounts.some(count => count >= 4)) {
      return 'PSS14'; // 4-pole speaker connectors get 14 AWG
    } else {
      // Default to 2-pole speaker cable
      return 'PSS16'; // 2-pole speaker connectors get 16 AWG
    }
  }
  
  // Check if any connector has 2 poles (TS, RCA, etc.)
  const hasTwoPoleConnector = poleCounts.some(count => count === 2);
  
  // Check if all connectors have 3 or more poles (XLR, TRS, etc.)
  const allThreeOrMorePoles = poleCounts.every(count => count >= 3);
  
  // Determine optimal wire type based on pole count logic
  if (hasTwoPoleConnector) {
    // If any connector has 2 poles, prefer PSI (Professional Shielded Instrument cable)
    return 'PSI';
  } else if (allThreeOrMorePoles) {
    // If all connectors have 3 or more poles, prefer PSM (Professional Shielded Microphone cable)
    return 'PSM';
  }
  
  // Default fallback
  return 'PSM';
}

// Check if wire type is optimal for the given connectors
export function isWireTypeOptimal(wireType, connectorIds) {
  const optimalWireType = getOptimalWireTypeForConnectors(connectorIds);
  return wireType === optimalWireType;
}

// Get wire type selection warning message
export function getWireTypeWarning(wireType, connectorIds) {
  if (!connectorIds || connectorIds.length === 0) return null;
  
  const optimalWireType = getOptimalWireTypeForConnectors(connectorIds);
  
  if (wireType !== optimalWireType) {
    const poleCounts = connectorIds.map(id => getConnectorPoleCount(id)).filter(count => count !== null);
    const hasTwoPoleConnector = poleCounts.some(count => count === 2);
    const allThreeOrMorePoles = poleCounts.every(count => count >= 3);
    
    // Check if any connector is a speaker connector
    const hasSpeakerConnector = connectorIds.some(id => {
      const connectorData = compatibilityMatrix[id];
      if (!connectorData) return false;
      
      const isSpeakerType = connectorData.type === 'speaker' || 
                           connectorData.type === 'Speakon' ||
                           connectorData.family === 'Speakon' ||
                           (connectorData.purpose && connectorData.purpose.toLowerCase().includes('speaker'));
      
      return isSpeakerType;
    });
    
    // Check if any connector is an XLR connector
    const hasXLRConnector = connectorIds.some(id => {
      const connectorData = compatibilityMatrix[id];
      if (!connectorData) return false;
      
      const isXLRType = connectorData.type === 'XLR' || 
                        connectorData.family === 'XLR' ||
                        connectorData.family === 'XLR 3-pin' ||
                        connectorData.family === 'XLR 5-pin' ||
                        (connectorData.description && connectorData.description.toLowerCase().includes('xlr'));
      
      return isXLRType;
    });
    
    // Provide specific warnings based on the mismatch
    if (hasSpeakerConnector && !wireType.startsWith('PSS')) {
      return `${wireType} wire used due to speaker connector`;
    } else if (hasTwoPoleConnector && wireType === 'PSM' && hasXLRConnector) {
      return `${wireType} wire used due to XLR connector, unbalanced audio`;
    } else if (hasTwoPoleConnector && wireType === 'PSM') {
      return `${wireType} wire used due to 2 pole connector`;
    } else if (!hasTwoPoleConnector && allThreeOrMorePoles && wireType === 'PSI') {
      return `${wireType} wire used due to 3+ pole connectors`;
    }
  }
  
  return null;
}

// Get default wire type for a connector (optionally respect initial cable type choice)
export function getDefaultWireType(connectorId, options = {}) {
  const availableWires = getAvailableWireTypes(connectorId);
  if (availableWires.length === 0) return null;

  // Respect user's initial cable type: Audio → prefer PSM; DMX → prefer PSDMX
  const cableType = options.cableType && options.cableType.toLowerCase();
  const priorityOrder = cableType === 'audio'
    ? ['PSM', 'PSI', 'PSS14', 'PSS12', 'PSS16']  // Audio: never prefer DMX wire
    : cableType === 'dmx'
      ? ['PSDMX', 'PSM', 'PSI', 'PSS14', 'PSS12', 'PSS16']
      : ['PSDMX', 'PSM', 'PSI', 'PSS14', 'PSS12', 'PSS16'];

  for (const priorityWire of priorityOrder) {
    const found = availableWires.find(wire => wire.id === priorityWire);
    if (found) return found.id;
  }

  // If no priority wire found, return the first available
  return availableWires[0].id;
}

// Get default wire type for multiple connectors (smart selection). Respects initial cable type when provided.
export function getDefaultWireTypeForConnectors(connectorIds, options = {}) {
  if (!connectorIds || connectorIds.length === 0) return null;

  const cableType = options.cableType && options.cableType.toLowerCase();

  // Get the optimal wire type based on pole counts (and connector purpose)
  let optimalWireType = getOptimalWireTypeForConnectors(connectorIds);

  // User chose "Audio Cable" at the start → never default to DMX wire
  if (cableType === 'audio' && optimalWireType === 'PSDMX') {
    optimalWireType = 'PSM';
  }
  // User chose "DMX Cable" at the start → prefer PSDMX when connectors support it
  if (cableType === 'dmx') {
    const allSupportDMX = connectorIds.every(connectorId => {
      const compatible = getConnectorWireCompatibility(connectorId);
      return compatible.includes('PSDMX');
    });
    if (allSupportDMX) return 'PSDMX';
  }

  const allConnectorsSupportOptimal = connectorIds.every(connectorId => {
    const compatibleWires = getConnectorWireCompatibility(connectorId);
    return compatibleWires.includes(optimalWireType);
  });

  if (allConnectorsSupportOptimal) {
    return optimalWireType;
  }

  // Fall back to connector-based default, passing through cable type so Audio never gets PSDMX
  return getDefaultWireType(connectorIds[0], options);
}

// Check if a wire type is compatible with a connector
export function isWireCompatible(connectorId, wireType) {
  const compatibleWires = getConnectorWireCompatibility(connectorId);
  return compatibleWires.includes(wireType);
}

// Get wire type description
export function getWireTypeDescription(wireType) {
  return wireTypes[wireType]?.description || 'Unknown wire type';
}

// Get wire type label
export function getWireTypeLabel(wireType) {
  return wireTypes[wireType]?.label || wireType;
} 